import { PermissionEnum } from '@Application/Security/Roles';
import { openContactSidebar } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/Signals/contactSidebarSignal';
import { openContactToDelete } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/Signals/contactToDeleteSignal';
import useIsBeneficiaryEditable from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/Hooks/useIsBeneficiaryEditable';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type BeneficiaryContactView from '@UI/Store/BeneficiaryContacts/Views/BeneficiaryContactView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin-top: auto;
`;

type Props = {
	readonly contact: BeneficiaryContactView;
};

/**
 * Leaf action component: it opens the edit sidebar and requests the delete confirmation purely
 * through their signals' setters — no `onEdit`/`onDelete` callback; the card itself stays visible
 * without `SUBJECT_WRITE`, only the actions disappear. The outer `div` is this card's own
 * subgrid track, not decorative filler: it stays rendered — empty — without `SUBJECT_WRITE`,
 * instead of collapsing the track and misaligning the card against its siblings.
 */
const ContactCardActions: React.FC<Props> = ({ contact }) => {
	const hasWritePermission = useHasPermission(PermissionEnum.SUBJECT_WRITE);
	const beneficiaryId = useSelector(
		(state) => state.beneficiaryContactsStore.contacts?.beneficiaryId ?? null,
	);
	const isEditable = useIsBeneficiaryEditable(beneficiaryId);
	const canWrite = hasWritePermission && isEditable;

	const handleEditClick = React.useCallback(() => {
		openContactSidebar(contact.id);
	}, [contact.id]);

	const handleDeleteClick = React.useCallback(() => {
		openContactToDelete(contact.id);
	}, [contact.id]);

	if (!canWrite) {
		return <div css={actionsRowStyle} />;
	}

	return (
		<div css={actionsRowStyle}>
			<Button
				type="button"
				size="sm"
				aria-label={`Modifier le contact ${contact.fullName}`}
				severity={'secondary'}
				onClick={handleEditClick}
			>
				Modifier
			</Button>
			<Button
				type="button"
				size="sm"
				aria-label={`Supprimer le contact ${contact.fullName}`}
				severity={'danger'}
				onClick={handleDeleteClick}
			>
				Supprimer
			</Button>
		</div>
	);
};

export default ContactCardActions;
